import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

const EmailHealthReportSkeleton = () => {
  return (
    <div className="w-full space-y-6 mb-6">
      {/* Header skeleton */}
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-8 w-64" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-9 w-32" />
        </div>
      </div>

      {/* Cards grid skeleton */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Score Card skeleton */}
        <Card>
          <CardHeader className="pb-2">
            <Skeleton className="h-5 w-32" />
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center">
              <Skeleton className="h-32 w-32 rounded-full" />
              <div className="w-full pt-4">
                <div className="flex flex-col gap-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center justify-between">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-4 w-10" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Status Card skeleton */}
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-5 w-32 mb-3" />
            <div className="mt-2">
              <div className="flex items-center justify-between mb-2">
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-6 w-6 rounded-full" />
              </div>
              <Skeleton className="h-5 w-full" />
            </div>
          </CardContent>
        </Card>

        {/* Status Summary Card skeleton */}
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-5 w-32 mb-3" />
            <div className="mt-3 space-y-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-2 rounded-md"
                >
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <Skeleton className="h-6 w-8" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Run Test Card skeleton */}
      <Card className="mt-5">
        <CardContent className="p-0">
          <div className="flex flex-col md:flex-row md:items-center justify-between p-6">
            <div className="mb-4 md:mb-0">
              <Skeleton className="h-6 w-40 mb-2" />
              <Skeleton className="h-4 w-64" />
            </div>
            <Skeleton className="h-10 w-36" />
          </div>
          <div className="px-6 py-3 border-t">
            <div className="flex items-center">
              <Skeleton className="h-4 w-4 mr-2 rounded-full" />
              <Skeleton className="h-4 w-full max-w-md" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Problems Card skeleton */}
      <Card className="mt-5 mb-4">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4 rounded-full" />
              <Skeleton className="h-5 w-32" />
            </div>
            <Skeleton className="h-4 w-4" />
          </div>
        </div>
      </Card>

      {/* Email Delivery Chain skeleton */}
      <div className="mb-6">
        <Skeleton className="h-6 w-48 mb-4" />
        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded-full" />
                <Skeleton className="h-5 w-32" />
              </div>
              <Skeleton className="h-4 w-4" />
            </div>
            <Skeleton className="h-4 w-64 mt-2" />
          </div>
        </Card>
      </div>

      {/* Email Health Results section skeleton */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-8 w-24" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="overflow-hidden shadow-none">
              <div className="flex items-center justify-between w-full px-4 py-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-2 w-2 rounded-full" />
                  <div>
                    <Skeleton className="h-5 w-24 mb-1" />
                    <Skeleton className="h-3 w-40" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Skeleton className="h-4 w-4 rounded-full" />
                  <Skeleton className="h-4 w-4" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Domain Age Card skeleton */}
      <div className="mb-6">
        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded-full" />
                <Skeleton className="h-5 w-24" />
              </div>
              <Skeleton className="h-4 w-4" />
            </div>
            <Skeleton className="h-4 w-64 mt-2" />
          </div>
        </Card>
      </div>

      {/* Domain Redirection skeleton */}
      <div className="mb-6">
        <Card>
          <div className="flex p-4 items-center justify-between">
            <div className="flex items-center space-x-2">
              <Skeleton className="h-4 w-4 rounded-full" />
              <Skeleton className="h-5 w-40" />
            </div>
            <div className="flex items-center space-x-3">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-4" />
            </div>
          </div>
        </Card>
      </div>

      {/* Blacklists section skeleton */}
      <div className="mb-6">
        <Skeleton className="h-6 w-32 mb-4" />
        <div className="grid gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-3 w-3 rounded-full" />
                  </div>
                  <Skeleton className="h-4 w-4" />
                </div>
                <Skeleton className="h-4 w-64 mt-2" />
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Open relay check skeleton */}
      <div className="">
        <Skeleton className="h-6 w-40 mb-4" />
        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded-full" />
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-3 w-3 rounded-full" />
              </div>
              <Skeleton className="h-4 w-4" />
            </div>
            <Skeleton className="h-4 w-64 mt-2" />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default EmailHealthReportSkeleton;
